import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { ApiError, errorMessage } from "../lib/api";
import { qk, useDeleteEntry, useEntry, useManifest, usePatchEntry, useRetryEntry } from "../lib/queries";
import { isInFlight } from "../lib/types";
import { useUploads } from "../lib/uploadQueue";
import { DevelopingCard } from "../components/DevelopingCard";
import { EntryCardSkeleton, LoadingAnnouncement, Skeleton } from "../components/Skeleton";
import { EntryDetails } from "../components/EntryDetails";
import { ErrorState } from "../components/ErrorState";
import { FailedNotice, FavouriteButton, RejectedNotice } from "../components/EntryCard";
import { FieldNote } from "../components/FieldNote";
import { PlaceEditor } from "../components/PlaceEditor";
import { ScrapbookPage } from "../components/ScrapbookPage";
import { useToasts } from "../components/Toasts";
import { NotFound } from "./NotFound";

/**
 * One find: the photograph, what it is, and what you have to say about it.
 * Everything Claude wrote beyond the headline facts lives one tap away, in
 * the details disclosure — this page leads with the photo and your notes.
 */
export function EntryDetail() {
  const { entryId } = useParams<{ entryId: string }>();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { push } = useToasts();
  const { previews } = useUploads();

  const entryQuery = useEntry(entryId, { poll: true });
  const manifest = useManifest();

  const patch = usePatchEntry(entryId ?? "");
  const remove = useDeleteEntry();
  const retry = useRetryEntry();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  if (entryQuery.error instanceof ApiError && entryQuery.error.isNotFound) {
    return <NotFound title="That page isn't in the book" />;
  }

  if (entryQuery.isPending) {
    return (
      <div className="mx-auto max-w-3xl">
        <LoadingAnnouncement>Fetching the page…</LoadingAnnouncement>
        <Skeleton className="mb-6 h-4 w-32" />
        <EntryCardSkeleton />
      </div>
    );
  }

  if (entryQuery.isError || !entryQuery.data) {
    return <ErrorState error={entryQuery.error} onRetry={() => void entryQuery.refetch()} />;
  }

  const entry = entryQuery.data;
  const refreshEntry = () => {
    void client.invalidateQueries({ queryKey: qk.entry(entry.id) });
  };

  const onDelete = () => {
    remove.mutate(entry.id, {
      onSuccess: () => {
        push({ title: "That page has been taken out of the book." });
        void navigate("/scrapbook", { replace: true });
      },
      onError: (error) =>
        push({ kind: "problem", title: "The page wouldn't come out.", body: errorMessage(error) }),
    });
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-5 flex items-center justify-between gap-4">
        <Link to="/scrapbook" className="touch inline-flex items-center gap-2 font-label text-sm text-ink-soft hover:text-ink">
          <span aria-hidden="true">←</span> The scrapbook
        </Link>
        <FavouriteButton
          favourite={entry.favourite}
          title={entry.displayTitle}
          onToggle={() => patch.mutate({ favourite: !entry.favourite })}
        />
      </div>

      {isInFlight(entry.status) ? (
        <div className="mx-auto max-w-sm">
          <DevelopingCard
            seedId={entry.id}
            previewUrl={previews[entry.id] ?? entry.thumbUrl ?? entry.imageUrl}
            phase={entry.status}
          />
        </div>
      ) : (
        <ScrapbookPage
          entry={entry}
          manifest={manifest.data}
          onImageError={refreshEntry}
        />
      )}

      {entry.status === "rejected" ? (
        <div className="sheet grain mt-6 p-5">
          <RejectedNotice
            reason={entry.rejectionReason}
            confirming={confirmingDelete}
            deleting={remove.isPending}
            onAskDelete={() => setConfirmingDelete(true)}
            onCancelDelete={() => setConfirmingDelete(false)}
            onConfirmDelete={onDelete}
          />
        </div>
      ) : null}

      {entry.status === "failed" ? (
        <div className="sheet grain mt-6 p-5">
          <FailedNotice
            error={entry.error}
            retrying={retry.isPending}
            onRetry={() =>
              retry.mutate(entry.id, {
                onSuccess: () => push({ title: "Back into the darkroom it goes." }),
                onError: (error) =>
                  push({ kind: "problem", title: "Couldn't requeue it.", body: errorMessage(error) }),
              })
            }
          />
        </div>
      ) : null}

      <FieldNote className="mt-8" entry={entry} />
      <PlaceEditor className="mt-6" entry={entry} />
      <EntryDetails className="mt-6" entry={entry} manifest={manifest.data} />

      {entry.status !== "rejected" ? (
        <div className="mt-6">
          {confirmingDelete ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-prose text-sm text-ink">
                Remove this page for good? The photograph goes too.
              </p>
              <button
                type="button"
                onClick={onDelete}
                disabled={remove.isPending}
                className="touch border border-rust px-3 py-1.5 font-label text-sm text-rust transition-colors hover:bg-[color-mix(in_oklab,var(--color-rust)_10%,transparent)] disabled:opacity-60"
              >
                {remove.isPending ? "Removing…" : "Yes, remove it"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="touch font-label text-sm text-ink-soft underline decoration-rule underline-offset-4 hover:text-ink"
              >
                Keep it
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="touch font-label text-sm text-ink-soft underline decoration-rule underline-offset-4 hover:text-rust"
            >
              Remove this page
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
